<script lang="ts">
    import { onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faCircleCheck, faPenToSquare } from '@fortawesome/free-solid-svg-icons'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import DynamicTableSignPad from '../customer-service-v2/blocks/view/dynamic-table-sign-pad.svelte'

    type Step = 'loading' | 'sign' | 'submitting' | 'done' | 'error'

    let step: Step = 'loading'
    let errorMsg = ''
    let data: {
        formTitle: string
        columnLabel: string
        blockId: string
        rowId: string
        columnId: string
        row: any
        columns: any[]
    } | null = null
    let token = ''
    let signatureDataUrl: string | null = null

    onMount(load)

    async function load() {
        try {
            const urlParams = new URLSearchParams(window.location.search)
            // "t", não "token": "token" já é usado em toda a SPA para o JWT da sessão logada, e um
            // usuário autenticado abrindo este link público colidia com esse outro parâmetro.
            token = urlParams.get('t') ?? ''
            if (!token) {
                errorMsg = 'Link inválido.'
                step = 'error'
                return
            }
            data = await window.api.tools.formsV2.forms.publicGetRowSign(token)
            step = 'sign'
        } catch {
            errorMsg = 'Este link não está mais disponível ou já foi utilizado.'
            step = 'error'
        }
    }

    async function submit() {
        if (!signatureDataUrl) return
        step = 'submitting'
        try {
            await window.api.tools.formsV2.forms.publicSubmitRowSign(token, signatureDataUrl)
            step = 'done'
        } catch {
            errorMsg = 'Não foi possível registrar a assinatura. Tente novamente.'
            step = 'sign'
        }
    }
</script>

<div class="row-sign-page">
    <div class="row-sign-card">
        <div class="card-logo">
            <span class="logo-text">zandu</span>
        </div>

        {#if step === 'loading'}
            <LoadSpinner show={true} />

        {:else if step === 'error'}
            <div class="state-icon error-icon">
                <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
            </div>
            <h2 class="state-title">Link indisponível</h2>
            <p class="state-sub">{errorMsg}</p>

        {:else if step === 'done'}
            <div class="state-icon done-icon">
                <Fa icon={faCircleCheck} size="3x" />
            </div>
            <h2 class="state-title">Assinatura registrada</h2>
            <p class="state-sub">Sua assinatura foi salva com sucesso. Você já pode fechar esta página.</p>

        {:else if data}
            <h1 class="form-title">{data.formTitle}</h1>
            <p class="column-label">
                Você foi convidado a assinar o campo <strong>{data.columnLabel}</strong>.
            </p>

            <div class="pad-wrap">
                <DynamicTableSignPad disabled={step === 'submitting'} on:change={(e) => (signatureDataUrl = e.detail)} />
            </div>

            <button
                type="button"
                class="submit-btn"
                disabled={!signatureDataUrl || step === 'submitting'}
                on:click={submit}
            >
                <Fa icon={faPenToSquare} />
                <span>{step === 'submitting' ? 'Enviando...' : 'Confirmar assinatura'}</span>
            </button>

            {#if errorMsg}
                <p class="submit-error">{errorMsg}</p>
            {/if}
        {/if}
    </div>
</div>

<style>
    .row-sign-page {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f4f5f7;
        padding: 24px 16px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    }
    .row-sign-card {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
        padding: 32px 28px;
        width: 100%;
        max-width: 480px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
    }
    .card-logo {
        margin-bottom: 4px;
    }
    .logo-text {
        font-size: 1.5rem;
        font-weight: 700;
        color: #1bdf6f;
        letter-spacing: -0.5px;
    }
    .form-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #212529;
        text-align: center;
        margin: 0;
    }
    .column-label {
        font-size: 0.9rem;
        color: #495057;
        text-align: center;
        margin: 0;
    }
    .pad-wrap {
        width: 100%;
        display: flex;
        justify-content: center;
    }
    .submit-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        border: none;
        border-radius: 10px;
        padding: 12px 28px;
        font-size: 0.95rem;
        font-weight: 600;
        background: #1bdf6f;
        color: #1f2937;
        cursor: pointer;
        width: 100%;
        justify-content: center;
    }
    .submit-btn:hover:not(:disabled) {
        background: #17be5e;
    }
    .submit-btn:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
    .submit-error {
        color: #c92a2a;
        font-size: 0.85rem;
        margin: 0;
        text-align: center;
    }
    .state-icon {
        font-size: 2rem;
    }
    .done-icon {
        color: #1bdf6f;
    }
    .error-icon {
        color: #fa5252;
    }
    .state-title {
        font-size: 1.15rem;
        font-weight: 600;
        color: #212529;
        margin: 0;
    }
    .state-sub {
        font-size: 0.88rem;
        color: #868e96;
        text-align: center;
        margin: 0;
    }
</style>
